import {
  FailedChecks,
  UserPreEvent,
  PaginatedEventRoleList,
  listOrganizationUser,
  listUser,
  User
} from '@goldcast/api/backend';
import logger from '@goldcast/logger';
import { IconChartBar, IconKey, IconSettings } from '@tabler/icons-vue';

import { Speaker, FormattedAlertForSpeakers, SpeakerColumnKey, SpeakerRoleEnum, ExtendedRole } from './types';

import { FormattedAlert } from '@/studio/alerts/types';
import { EmailTypes } from '@/studio/emailSchedule/store/types';
import { getEnv } from '@/utils/env';

export const SPEAKERS_COLUMNS: {
  name: string;
  key: SpeakerColumnKey;
  allowFiltering?: boolean;
}[] = [
  {
    name: 'Full Name',
    key: 'full_name'
  },
  {
    name: 'Title',
    key: 'title'
  },
  {
    name: 'Company',
    allowFiltering: true,
    key: 'company'
  },
  {
    name: 'Permission',
    key: 'permission'
  },
  {
    name: 'Magic Link',
    key: 'magic_link'
  }
];

export const getInitialState = () => ({
  speakers: {},
  isSpeakerListLoading: false,
  selectedSpeakers: {} as Record<number, Speaker>,
  areAllSpeakersSelected: false,
  speakerCurrentPage: 1,
  speakerColumns: SPEAKERS_COLUMNS,
  speakerPerPage: 25,
  totalSpeakerCount: 0,
  isSpeakerEmailConfigLoading: false,
  speakerInviteEmailConfig: null,
  excludedSpeakers: {} as Record<number, Speaker>,
  speakerRolesMap: {} as Record<string, ExtendedRole>,
  systemUserRolesMap: {} as Record<string, ExtendedRole>,
  defaultSpeakerRole: null
});

export const STATIC_ASSETS = {
  ORGANIZATION_PREFIX: 'organization',
  EVENT_PREFIX: 'event',
  ORG_PREFIX: 'organization',
  LOGO_PATH: 'logo',
  BANNER_PATH: 'banner',
  SKINNED_LOGO: 'skinlogos',
  SKINNED_BANNERS: 'skinnedBanners',
  REGISTRATION_BANNER_PATH: 'registration_banner',
  SPONSOR_LOGOS_PATH: 'sponsor_logos',
  RESOURCES_PATH: 'resources',
  IMAGE_PATH: 'images',
  SITEDATA_PATH: 'regappsitedata',
  VIDEOS_PATH: 'videos',
  VIDEOS_THUMBNAIL_PATH: 'thumbnails',
  FILLER_SLIDE_IMAGES: 'slide-filler-images',
  CLOUDFRONT_URL: getEnv('VUE_APP_CLOUDFRONT_URL'),
  RESIZED_MEDIUM_USER_IMAGE_S3_FOLDER: 'user-resized-medium',
  ADMIN_PREFIX: 'admin'
};

export function transformServerSpeakers(speakers: Speaker[] | PaginatedEventRoleList) {
  const transformedSpeakers: Record<number, Speaker> = {};
  Array.isArray(speakers) &&
    speakers.forEach((speaker: Speaker) => {
      if (speaker?.organization_user?.profile_picture_url) {
        let profilePictureUrl: string | null = '';
        if (isCloudFrontURL(speaker.organization_user.profile_picture_url)) {
          profilePictureUrl = speaker.organization_user.profile_picture_url;
        } else {
          profilePictureUrl = getProfileImage(speaker.organization_user.profile_picture_url);
        }

        speaker.organization_user.profile_picture_url = profilePictureUrl;
        speaker.isFetchingMagicLink = false;
      }

      transformedSpeakers[speaker.id] = {
        ...speaker
      };
    });
  return transformedSpeakers;
}

export function getSubTitle(title, company) {
  if (title && company) {
    return `${title}, ${company}`;
  }
  if (title) {
    return title;
  }
  if (company) {
    return company;
  }
  return '';
}

export function isLinkedInURL(url: string): boolean {
  if (!url) {
    return true;
  }
  const regex = /linkedin\.com\/(in|company)\/[a-zA-Z0-9-]{1,}/i;
  return regex.test(url);
}

export function isTwitterURL(url: string): boolean {
  if (!url) {
    return true;
  }
  const regex = /^https?:\/\/(www\.)?(twitter|x)\.com\/([a-zA-Z0-9_]){1,15}\/?$/i;
  return regex.test(url);
}

// todo change method name later
export function getProfileImage(profilePictureUrl: string) {
  return profilePictureUrl ? `${STATIC_ASSETS.CLOUDFRONT_URL}/${profilePictureUrl}` : null;
}

export function isCloudFrontURL(url: string): boolean {
  const goldcastRegex = /^https?:\/\/([a-zA-Z0-9_-]+\.)?staticassets\.goldcast\.io\//i;
  const cloudfrontRegex = /^https?:\/\/([a-zA-Z0-9_-]+\.)?cloudfront\.net\//i;
  const goldcastEURegex = /^https?:\/\/([a-zA-Z0-9_-]+\.)?staticassets\.eu\.goldcast\.io\//i;
  const publicSg50Regex = /^https?:\/\/([a-zA-Z0-9_-]+\.)?staticassets\.public-sg50\.goldcast\.io\//i;
  const publicDv50Regex = /^https?:\/\/([a-zA-Z0-9_-]+\.)?staticassets\.public-dv50\.goldcast\.io\//i;
  const publicPr50Regex = /^https?:\/\/([a-zA-Z0-9_-]+\.)?staticassets\.public-pr50\.goldcast\.io\//i;
  const publicPr53Regex = /^https?:\/\/([a-zA-Z0-9_-]+\.)?staticassets\.public-pr53\.goldcast\.io\//i;
  return (
    goldcastRegex.test(url) ||
    cloudfrontRegex.test(url) ||
    goldcastEURegex.test(url) ||
    publicSg50Regex.test(url) ||
    publicDv50Regex.test(url) ||
    publicPr50Regex.test(url) ||
    publicPr53Regex.test(url)
  );
}

export function isBlobUrl(url: string): boolean {
  const regex = /^blob:/i;
  return regex.test(url);
}

export function addFormattedAlertToSpeakerId(
  formattedAlertsForSpeakers: FormattedAlertForSpeakers,
  userId: string,
  formattedAlert: FormattedAlert
) {
  if (formattedAlertsForSpeakers[userId]) {
    formattedAlertsForSpeakers[userId].push(formattedAlert);
  } else {
    formattedAlertsForSpeakers[userId] = [formattedAlert];
  }
}

const failedCheckKeyToAlertMessageMap = {
  speakers_not_added_to_any_broadcasts: 'Speaker is not added to any broadcasts.',
  speaker_not_completed_tech_check: 'Speaker has not completed the tech check.'
};

export function formatFailedChecksForSpeakers(failedChecks: FailedChecks | {} = {}): FormattedAlertForSpeakers {
  const formattedAlertsForSpeakers: FormattedAlertForSpeakers = {};

  Object.keys(failedChecks).forEach(key => {
    const failedCheck = failedChecks[key];
    const failedCheckKey = key as keyof FailedChecks;

    switch (failedCheckKey) {
      case 'speakers_not_added_to_any_broadcasts':
      case 'speaker_not_completed_tech_check':
        (failedCheck as UserPreEvent[]).forEach((c, i) => {
          const key = `${failedCheckKey}_${c.user_id}${i}`;
          const formattedAlert: FormattedAlert = {
            key: key,
            message: failedCheckKeyToAlertMessageMap[failedCheckKey],
            actionRequired: !!c.action_required
          };
          addFormattedAlertToSpeakerId(formattedAlertsForSpeakers, c.user_id, formattedAlert);
        });
        break;
      default:
    }
  });

  return formattedAlertsForSpeakers;
}

export const SpeakerEmailTypes: EmailTypes[] = ['SpkInvite', 'Spk-Reminder-1h', 'Spk-Reminder-1d'];

export const SpeakerRoleDescriptionMap = {
  [SpeakerRoleEnum.StageAccess]: {
    description: 'Go onstage and view Q&A.',
    icon: IconKey
  },
  [SpeakerRoleEnum.ShareAccess]: {
    description: 'Go onstage, answer Q&A, and share slides and screen.',
    icon: IconChartBar
  },
  [SpeakerRoleEnum.FullAccess]: {
    description: 'All event production controls, including moderation settings.',
    icon: IconSettings
  }
};

export async function fetchUserByEmailAndOrgId(email: string, organizationId: string): Promise<User | null> {
  const lowerCaseEmail = email.toLowerCase();
  if (!lowerCaseEmail) {
    logger.warn('[store/utils] fetchUserByEmail', 'Email is required');
    return null;
  }
  if (!organizationId) {
    logger.warn('[store/utils] fetchUserByEmail', 'Organization ID is required');
    return null;
  }
  try {
    const orgUsers = (await listOrganizationUser({
      queryParams: {
        user__email: lowerCaseEmail,
        organization: organizationId
      }
    })) as User[];
    if (orgUsers.length > 0) {
      return orgUsers[0];
    }
    const users = (await listUser({ queryParams: { email: lowerCaseEmail } })) as User[];
    return users.length > 0 ? users[0] : null;
  } catch (err) {
    logger.error('[store/utils] fetchUserByEmail', 'Error fetching user by email', { error: err });
    return null;
  }
}
